import { Component, OnInit, OnDestroy, HostListener, ViewChild, ElementRef } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { ClipboardHelper } from '@piqet-angular/shared';

export interface LegendItem {
  label: string;
  description: string;
  type: string;
}

export interface EndpointItem {
  name: string;
  method: string;
  path: string;
}

export interface SingleIndicatorItem {
  name: string;
}

export interface IndicatorSetItem {
  name: string;
  description: string;
}

export interface TransportItem {
  mode: string;
  description: string;
}

export interface PackingLevelItem {
  level: number;
  name: string;
  description: string;
}

export interface StorageTypeItem {
  name: string;
}

export interface StorageCategory {
  name: string;
  items: StorageTypeItem[];
}

export interface MaterialItem {
  name: string;
}

export interface MaterialCategory {
  name: string;
  items: MaterialItem[];
}

export interface FillingProcessItem {
  name: string;
}

export interface QuickReferenceData {
  title: string;
  subtitle: string;
  sections: {
    legend: {
      id: string;
      title: string;
      items: LegendItem[];
    };
    apiEndpoints: {
      id: string;
      title: string;
      items: EndpointItem[];
    };
    singleIndicators: {
      id: string;
      title: string;
      items: SingleIndicatorItem[];
    };
    indicatorSets: {
      id: string;
      title: string;
      items: IndicatorSetItem[];
    };
    transportModes: {
      id: string;
      title: string;
      items: TransportItem[];
    };
    packingLevels: {
      id: string;
      title: string;
      items: PackingLevelItem[];
    };
    storageTypes: {
      id: string;
      title: string;
      categories: StorageCategory[];
    };
    materials: {
      id: string;
      title: string;
      categories: MaterialCategory[];
    };
    fillingProcess: {
      id: string;
      title: string;
      items: FillingProcessItem[];
    };
  };
}

export interface TocSection {
  id: string;
  title: string;
}

interface NavSection {
  title: string;
  children?: NavChild[];
  items?: NavItem[];
}

interface NavItem {
  label: string;
  isExpanded: boolean;
  children: NavChild[];
}

interface NavChild {
  label: string;
  method?: string;
  methodClass?: string;
  isActive: boolean;
  route?: string;
  sectionId?: string;
}

@Component({
  selector: 'piqet-angular-quick-reference',
  templateUrl: './quick-reference.component.html',
  styleUrls: ['./quick-reference.component.scss'],
})
export class QuickReferenceComponent implements OnInit, OnDestroy {
  @ViewChild('searchInput') searchInput?: ElementRef<HTMLInputElement>;

  data: QuickReferenceData | null = null;
  highlightedSection: string | null = null;
  activeSection: string | null = null;
  searchQuery = '';
  private routeSub: Subscription | null = null;

  tocSections: TocSection[] = [];
  expandedIndicatorSections: Set<string> = new Set();

  navSections: NavSection[] = [
    {
      title: 'Authentication',
      children: [
        { label: 'JWT authentication', isActive: false, route: '/docs/lca-api/auth' },
      ],
    },
    {
      title: 'LCA calculations',
      children: [
        { label: 'Impact calculator', isActive: false, route: '/docs/lca-api' },
      ],
    },
    {
      title: 'Quick reference',
      items: [
        {
          label: 'Reference data',
          isExpanded: true,
          children: [
            { label: 'Indicators', isActive: true, sectionId: 'indicators' },
            { label: 'Transport modes', isActive: false, sectionId: 'transport-modes' },
            { label: 'Packing levels', isActive: false, sectionId: 'packing-levels' },
            { label: 'Storage types', isActive: false, sectionId: 'storage-types' },
            { label: 'Filling process', isActive: false, sectionId: 'fillingProcess' },
            { label: 'Materials', isActive: false, sectionId: 'materials' },
          ],
        },
      ],
    },
  ];

  constructor(
    private readonly http: HttpClient,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    public clipboardHelper: ClipboardHelper,
  ) {}

  // --- Navigation ---

  navigateToRoute(route: string): void {
    this.router.navigateByUrl(route);
  }

  @HostListener('document:keydown', ['$event'])
  handleKeyboardShortcut(event: KeyboardEvent): void {
    if (event.key !== '/') return;
    const target = event.target as HTMLElement | null;
    const tag = target?.tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA' || target?.isContentEditable) return;
    event.preventDefault();
    this.searchInput?.nativeElement.focus();
  }

  toggleNavItem(item: NavItem): void {
    item.isExpanded = !item.isExpanded;
  }

  setActiveNavChild(child: NavChild): void {
    this.navSections.forEach(section => {
      section.children?.forEach(c => (c.isActive = false));
      section.items?.forEach(item => {
        item.children.forEach(c => (c.isActive = false));
      });
    });
    child.isActive = true;

    if (child.route) {
      this.router.navigateByUrl(child.route);
    } else if (child.sectionId) {
      this.scrollToSection(child.sectionId);
    }
  }

  get filteredNavSections(): NavSection[] {
    if (!this.searchQuery.trim()) return this.navSections;
    const q = this.searchQuery.toLowerCase();
    return this.navSections
      .map(section => ({
        ...section,
        children: section.children?.filter(c => c.label.toLowerCase().includes(q)),
        items: section.items
          ?.map(item => ({
            ...item,
            isExpanded: true,
            children: item.children.filter(c => c.label.toLowerCase().includes(q)),
          }))
          .filter(item => item.children.length > 0),
      }))
      .filter(
        section => (section.children?.length || 0) > 0 || (section.items?.length || 0) > 0
      );
  }

  // --- Lifecycle ---

  ngOnInit(): void {
    this.loadQuickReferenceData();

    this.routeSub = this.route.queryParams.subscribe(params => {
      if (params['section']) {
        this.highlightedSection = params['section'];
        this.scrollToSection(params['section']);
        setTimeout(() => {
          this.highlightedSection = null;
        }, 3000);
      }
    });
  }

  ngOnDestroy(): void {
    if (this.routeSub) {
      this.routeSub.unsubscribe();
    }
  }

  loadQuickReferenceData(): void {
    this.http.get<QuickReferenceData>('assets/json/quick-reference.json').subscribe({
      next: data => {
        this.data = data;
        this.initializeTocSections();
        setTimeout(() => {
          if (this.highlightedSection) {
            this.scrollToSection(this.highlightedSection);
          }
          if (this.tocSections.length > 0 && !this.activeSection) {
            this.activeSection = this.tocSections[0].id;
          }
        }, 100);
      },
      error: error => {
        console.error('Error loading quick reference data:', error);
      },
    });
  }

  initializeTocSections(): void {
    if (!this.data) return;
    this.tocSections = [
      { id: 'indicators', title: 'Indicators' },
      { id: this.data.sections.transportModes.id, title: this.data.sections.transportModes.title },
      { id: this.data.sections.packingLevels.id, title: this.data.sections.packingLevels.title },
      { id: this.data.sections.storageTypes.id, title: this.data.sections.storageTypes.title },
      { id: this.data.sections.fillingProcess.id, title: this.data.sections.fillingProcess.title },
      { id: this.data.sections.materials.id, title: this.data.sections.materials.title },
    ];
  }

  // --- Sections ---

  scrollToSection(sectionId: string): void {
    this.activeSection = sectionId;
    setTimeout(() => {
      const element = document.getElementById(sectionId);
      if (element) {
        const headerOffset = 80;
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
        window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
        this.highlightedSection = sectionId;
        setTimeout(() => {
          this.highlightedSection = null;
        }, 2000);
      }
    }, 50);
  }

  isHighlighted(sectionId: string): boolean {
    return this.highlightedSection === sectionId;
  }

  toggleIndicatorSection(sectionId: string): void {
    if (this.expandedIndicatorSections.has(sectionId)) {
      this.expandedIndicatorSections.delete(sectionId);
    } else {
      this.expandedIndicatorSections.add(sectionId);
    }
  }

  isIndicatorSectionExpanded(sectionId: string): boolean {
    return this.expandedIndicatorSections.has(sectionId);
  }

  // --- Clipboard ---

  copyToClipboard(text: string, event: Event): void {
    this.clipboardHelper.copyToClipboard(text, event);
  }

  isCopied(text: string): boolean {
    return this.clipboardHelper.isCopied(text);
  }
}
